import {
  SPORTS_BALL_PRESETS,
  computeBallForces,
  interpolateCurve,
  resolveSportsBallSpec,
  type SportsBallId,
  type SportsBallSpec,
} from './sportsBall';

export type PenaltyOutcome = 'goal' | 'saved-by-net' | 'post' | 'crossbar' | 'over' | 'wide' | 'short';
export type PenaltyShotPresetId = 'driven' | 'curl' | 'chip' | 'wide';

export interface PenaltyShotConfig {
  ballId: SportsBallId;
  shotPresetId: PenaltyShotPresetId;
  speedMps: number;
  horizontalAngleDeg: number;
  verticalAngleDeg: number;
  impactOffsetM: number;
  spinRpm: number;
  dragScale: number;
  magnusScale: number;
}

export interface PenaltyBallFrame {
  t: number;
  position: readonly [number, number, number];
  velocity: readonly [number, number, number];
  speedMps: number;
}

export interface NetNodeFrame {
  x: number;
  y: number;
  z: number;
  displacementM: number;
}

export interface NetImpact {
  timeS: number;
  ballPosition: readonly [number, number, number];
  impulseNs: number;
  peakNetDepthM: number;
}

export interface PenaltySimulationResult {
  config: PenaltyShotConfig;
  spec: SportsBallSpec;
  outcome: PenaltyOutcome;
  scored: boolean;
  ballFrames: PenaltyBallFrame[];
  netFrames: NetNodeFrame[][];
  netImpacts: NetImpact[];
  firstGroundBounceTimeS?: number;
  finalBallPosition: readonly [number, number, number];
  peakNetDepthM: number;
  entry?: {
    timeS: number;
    xM: number;
    yM: number;
    speedMps: number;
  };
}

interface NetNode {
  restX: number;
  restY: number;
  restZ: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  anchored: boolean;
}

const GOAL_WIDTH_M = 7.32;
const GOAL_HEIGHT_M = 2.44;
const PENALTY_DISTANCE_M = 11;
const GRAVITY_MPS2 = 9.80665;
const NET_PLANE_Z_M = 0.58;
const NET_COLUMNS = 13;
const NET_ROWS = 7;
const NET_MASS_KG = 0.095;
const NET_STIFFNESS = 115;
const NET_DAMPING = 2.4;
const NET_COUPLING = 0.18;
const NET_RESTITUTION = 0.18;
const SIM_DT = 1 / 240;
const SIM_DURATION_S = 6;
const FRAME_STRIDE = 3;
const POST_BOUNCE_DURATION_S = 1;

export const DEFAULT_PENALTY_SHOT: PenaltyShotConfig = {
  ballId: 'football',
  shotPresetId: 'driven',
  speedMps: 28,
  horizontalAngleDeg: 0,
  verticalAngleDeg: 8,
  impactOffsetM: 0,
  spinRpm: 520,
  dragScale: 1,
  magnusScale: 1,
};

export const PENALTY_SHOT_PRESETS: Readonly<Record<PenaltyShotPresetId, {
  label: string;
  note: string;
}>> = {
  driven: { label: 'Driven', note: 'low hard hit' },
  curl: { label: 'Curler', note: 'side-foot bend' },
  chip: { label: 'Chip', note: 'lofted drop' },
  wide: { label: 'Wide test', note: 'miss by angle' },
};

const BALL_SHOWCASE_SHOTS: Readonly<Record<SportsBallId, Record<PenaltyShotPresetId, Partial<PenaltyShotConfig>>>> = {
  football: {
    driven: { speedMps: 31, horizontalAngleDeg: -1.5, verticalAngleDeg: 6.5, impactOffsetM: 0.006, spinRpm: 420, dragScale: 1, magnusScale: 1 },
    curl: { speedMps: 27, horizontalAngleDeg: -7.5, verticalAngleDeg: 8.5, impactOffsetM: 0.048, spinRpm: 1350, dragScale: 1, magnusScale: 1.25 },
    chip: { speedMps: 18, horizontalAngleDeg: 1.2, verticalAngleDeg: 19, impactOffsetM: -0.012, spinRpm: 760, dragScale: 1.05, magnusScale: 0.85 },
    wide: { speedMps: 32, horizontalAngleDeg: 20.5, verticalAngleDeg: 7, impactOffsetM: 0.02, spinRpm: 700, dragScale: 1, magnusScale: 1 },
  },
  basketball: {
    driven: { speedMps: 19, horizontalAngleDeg: 0.8, verticalAngleDeg: 13.5, impactOffsetM: -0.018, spinRpm: 260, dragScale: 1.05, magnusScale: 0.8 },
    curl: { speedMps: 17, horizontalAngleDeg: 5.5, verticalAngleDeg: 15, impactOffsetM: -0.06, spinRpm: 620, dragScale: 1.1, magnusScale: 1.25 },
    chip: { speedMps: 13.5, horizontalAngleDeg: -1.5, verticalAngleDeg: 24, impactOffsetM: 0.025, spinRpm: 520, dragScale: 1.2, magnusScale: 0.95 },
    wide: { speedMps: 21, horizontalAngleDeg: -21, verticalAngleDeg: 11, impactOffsetM: -0.04, spinRpm: 520, dragScale: 1.05, magnusScale: 0.9 },
  },
  tennis: {
    driven: { speedMps: 43, horizontalAngleDeg: 3.5, verticalAngleDeg: 3.5, impactOffsetM: 0.03, spinRpm: 1350, dragScale: 1, magnusScale: 1.05 },
    curl: { speedMps: 38, horizontalAngleDeg: 10, verticalAngleDeg: 5, impactOffsetM: 0.07, spinRpm: 2750, dragScale: 1, magnusScale: 1.35 },
    chip: { speedMps: 25, horizontalAngleDeg: -2, verticalAngleDeg: 20.5, impactOffsetM: -0.045, spinRpm: 2200, dragScale: 1.15, magnusScale: 1.2 },
    wide: { speedMps: 46, horizontalAngleDeg: 17.5, verticalAngleDeg: 4, impactOffsetM: 0.055, spinRpm: 2500, dragScale: 1, magnusScale: 1.1 },
  },
};

export function createPenaltyShotConfig(
  ballId: SportsBallId,
  shotPresetId: PenaltyShotPresetId = 'driven',
): PenaltyShotConfig {
  const preset = BALL_SHOWCASE_SHOTS[ballId][shotPresetId];
  return validatePenaltyConfig({ ...DEFAULT_PENALTY_SHOT, ballId, shotPresetId, ...preset });
}

export const PENALTY_GOAL = {
  widthM: GOAL_WIDTH_M,
  heightM: GOAL_HEIGHT_M,
  penaltyDistanceM: PENALTY_DISTANCE_M,
  netPlaneZM: NET_PLANE_Z_M,
  netColumns: NET_COLUMNS,
  netRows: NET_ROWS,
} as const;

export function simulatePenaltyShootout(
  config: Partial<PenaltyShotConfig> = {},
): PenaltySimulationResult {
  const ballId = config.ballId ?? DEFAULT_PENALTY_SHOT.ballId;
  const shotPresetId = config.shotPresetId ?? DEFAULT_PENALTY_SHOT.shotPresetId;
  const resolved = validatePenaltyConfig({ ...createPenaltyShotConfig(ballId, shotPresetId), ...config });
  const spec = resolvePenaltyBallSpec(resolved);
  const nodes = createNetNodes();
  const ballFrames: PenaltyBallFrame[] = [];
  const netFrames: NetNodeFrame[][] = [];
  const netImpacts: NetImpact[] = [];
  const position = [0, spec.radiusM + 0.1, -PENALTY_DISTANCE_M];
  const velocity = launchVelocity(resolved);
  const spin = launchSpin(resolved, spec);
  let outcome: PenaltyOutcome | undefined;
  let entry: PenaltySimulationResult['entry'];
  let netContactActive = false;
  let peakNetDepthM = 0;
  let firstGroundBounceTimeS: number | undefined;

  const steps = Math.ceil(SIM_DURATION_S / SIM_DT);
  for (let step = 0; step <= steps; step += 1) {
    const t = step * SIM_DT;
    if (step % FRAME_STRIDE === 0) {
      ballFrames.push(createBallFrame(t, position, velocity));
      netFrames.push(snapshotNet(nodes));
    }

    if (firstGroundBounceTimeS !== undefined && t > firstGroundBounceTimeS + POST_BOUNCE_DURATION_S) break;

    const beforeZ = position[2];
    const forces = computeBallForces(spec, asVec3(velocity), asVec3(spin));
    velocity[0] += (forces.totalForce[0] / spec.massKg) * SIM_DT;
    velocity[1] += ((forces.totalForce[1] / spec.massKg) - GRAVITY_MPS2) * SIM_DT;
    velocity[2] += (forces.totalForce[2] / spec.massKg) * SIM_DT;
    position[0] += velocity[0] * SIM_DT;
    position[1] += velocity[1] * SIM_DT;
    position[2] += velocity[2] * SIM_DT;

    stepNet(nodes, SIM_DT);
    peakNetDepthM = Math.max(peakNetDepthM, measurePeakNetDepth(nodes));

    if (!entry && beforeZ < 0 && position[2] >= 0) {
      entry = {
        timeS: t,
        xM: position[0],
        yM: position[1],
        speedMps: length(velocity),
      };
      outcome = classifyGoalLineEntry(position, spec.radiusM);
    }

    const insideNetFace = Math.abs(position[0]) <= GOAL_WIDTH_M / 2 + spec.radiusM
      && position[1] >= spec.radiusM * 0.35
      && position[1] <= GOAL_HEIGHT_M + spec.radiusM;
    const touchingNet = insideNetFace
      && position[2] + spec.radiusM >= NET_PLANE_Z_M
      && position[2] - spec.radiusM <= NET_PLANE_Z_M + 0.32
      && velocity[2] > 0;

    if (touchingNet && !netContactActive) {
      const impulse = resolveNetImpact(nodes, position, velocity, spec.massKg, spec.radiusM);
      peakNetDepthM = Math.max(peakNetDepthM, measurePeakNetDepth(nodes));
      netImpacts.push({
        timeS: t,
        ballPosition: asVec3(position),
        impulseNs: impulse,
        peakNetDepthM,
      });
      netContactActive = true;
      outcome = 'saved-by-net';
    } else if (!touchingNet) {
      netContactActive = false;
    }

    if (resolveGroundContact(spec, position, velocity) && firstGroundBounceTimeS === undefined) {
      firstGroundBounceTimeS = t;
    }
  }

  return {
    config: resolved,
    spec,
    outcome: outcome ?? (entry ? 'goal' : 'short'),
    scored: Boolean(entry && classifyGoalLineEntry([entry.xM, entry.yM, 0], spec.radiusM) === 'goal'),
    ballFrames,
    netFrames,
    netImpacts,
    firstGroundBounceTimeS,
    finalBallPosition: asVec3(position),
    peakNetDepthM,
    entry,
  };
}

function resolveGroundContact(
  spec: SportsBallSpec,
  position: number[],
  velocity: number[],
): boolean {
  if (position[1] > spec.radiusM || velocity[1] >= 0) return false;

  const impactSpeed = -velocity[1];
  const restitution = interpolateCurve(spec.material.restitutionByImpactSpeed, impactSpeed);
  const horizontalRetention = Math.max(
    0.55,
    1 - spec.material.friction * 0.18 - spec.material.rollingResistance * 4,
  );

  position[1] = spec.radiusM;
  velocity[0] *= horizontalRetention;
  velocity[2] *= horizontalRetention;
  velocity[1] = impactSpeed < 0.45 ? 0 : impactSpeed * restitution;
  return impactSpeed >= 0.45;
}

export function validatePenaltyConfig(config: PenaltyShotConfig): PenaltyShotConfig {
  if (!SPORTS_BALL_PRESETS[config.ballId]) throw new RangeError('Penalty ballId must be a sports ball preset.');
  if (!PENALTY_SHOT_PRESETS[config.shotPresetId]) throw new RangeError('Penalty shotPresetId must be a known shot preset.');
  for (const [name, value] of [
    ['speedMps', config.speedMps],
    ['horizontalAngleDeg', config.horizontalAngleDeg],
    ['verticalAngleDeg', config.verticalAngleDeg],
    ['impactOffsetM', config.impactOffsetM],
    ['spinRpm', config.spinRpm],
    ['dragScale', config.dragScale],
    ['magnusScale', config.magnusScale],
  ] as const) {
    if (!Number.isFinite(value)) throw new RangeError(`Penalty ${name} must be finite.`);
  }
  if (config.speedMps < 5 || config.speedMps > 60) throw new RangeError('Penalty speedMps must be between 5 and 60.');
  if (Math.abs(config.horizontalAngleDeg) > 22) throw new RangeError('Penalty horizontalAngleDeg must be within +/-22.');
  if (config.verticalAngleDeg < -2 || config.verticalAngleDeg > 28) throw new RangeError('Penalty verticalAngleDeg must be between -2 and 28.');
  if (Math.abs(config.impactOffsetM) > 0.08) throw new RangeError('Penalty impactOffsetM must be within +/-0.08 m.');
  if (Math.abs(config.spinRpm) > 3200) throw new RangeError('Penalty spinRpm must be within +/-3200.');
  if (config.dragScale < 0 || config.dragScale > 2) throw new RangeError('Penalty dragScale must be between 0 and 2.');
  if (config.magnusScale < 0 || config.magnusScale > 2) throw new RangeError('Penalty magnusScale must be between 0 and 2.');
  return config;
}

function resolvePenaltyBallSpec(config: PenaltyShotConfig): SportsBallSpec {
  const base = SPORTS_BALL_PRESETS[config.ballId];
  return resolveSportsBallSpec(config.ballId, {
    aero: {
      dragByReynolds: base.aero.dragByReynolds.map((point) => ({ ...point, y: point.y * config.dragScale })),
      liftBySpinRatio: base.aero.liftBySpinRatio.map((point) => ({ ...point, y: point.y * config.magnusScale })),
    },
  });
}

function launchVelocity(config: PenaltyShotConfig): [number, number, number] {
  const yaw = radians(config.horizontalAngleDeg);
  const pitch = radians(config.verticalAngleDeg);
  const planarSpeed = config.speedMps * Math.cos(pitch);
  return [
    planarSpeed * Math.sin(yaw),
    config.speedMps * Math.sin(pitch),
    planarSpeed * Math.cos(yaw),
  ];
}

function launchSpin(config: PenaltyShotConfig, spec: SportsBallSpec): [number, number, number] {
  const spinRadS = config.spinRpm * 2 * Math.PI / 60;
  const strikeBias = Math.max(-1, Math.min(1, config.impactOffsetM / 0.08));
  const angleBias = Math.max(-0.35, Math.min(0.35, config.horizontalAngleDeg / 22));
  const sideSpin = spinRadS * (strikeBias + angleBias);
  const topSpin = -Math.abs(config.impactOffsetM) * config.speedMps / Math.max(spec.radiusM ** 2, 1e-6);
  return [topSpin, sideSpin, 0];
}

function createNetNodes(): NetNode[] {
  const nodes: NetNode[] = [];
  for (let row = 0; row < NET_ROWS; row += 1) {
    const y = (row / (NET_ROWS - 1)) * GOAL_HEIGHT_M;
    for (let col = 0; col < NET_COLUMNS; col += 1) {
      const x = -GOAL_WIDTH_M / 2 + (col / (NET_COLUMNS - 1)) * GOAL_WIDTH_M;
      const anchored = row === 0 || row === NET_ROWS - 1 || col === 0 || col === NET_COLUMNS - 1;
      nodes.push({ restX: x, restY: y, restZ: NET_PLANE_Z_M, x, y, z: NET_PLANE_Z_M, vx: 0, vy: 0, vz: 0, anchored });
    }
  }
  return nodes;
}

function stepNet(nodes: NetNode[], dt: number): void {
  const nextVelocity = nodes.map((node) => ({ vx: node.vx, vy: node.vy, vz: node.vz }));
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.anchored) {
      node.x = node.restX;
      node.y = node.restY;
      node.z = node.restZ;
      node.vx = 0;
      node.vy = 0;
      node.vz = 0;
      continue;
    }
    const springX = (node.restX - node.x) * NET_STIFFNESS;
    const springY = (node.restY - node.y) * NET_STIFFNESS;
    const springZ = (node.restZ - node.z) * NET_STIFFNESS;
    const dampingX = -node.vx * NET_DAMPING;
    const dampingY = -node.vy * NET_DAMPING;
    const dampingZ = -node.vz * NET_DAMPING;
    nextVelocity[index].vx += ((springX + dampingX) / NET_MASS_KG) * dt;
    nextVelocity[index].vy += ((springY + dampingY) / NET_MASS_KG) * dt;
    nextVelocity[index].vz += ((springZ + dampingZ) / NET_MASS_KG) * dt;
  }
  for (let row = 1; row < NET_ROWS - 1; row += 1) {
    for (let col = 1; col < NET_COLUMNS - 1; col += 1) {
      const index = row * NET_COLUMNS + col;
      const node = nodes[index];
      const neighbors = [index - 1, index + 1, index - NET_COLUMNS, index + NET_COLUMNS];
      const neighborZ = neighbors.reduce((total, neighbor) => total + nodes[neighbor].z, 0) / neighbors.length;
      nextVelocity[index].vz += (neighborZ - node.z) * NET_COUPLING;
    }
  }
  nodes.forEach((node, index) => {
    if (node.anchored) return;
    node.vx = nextVelocity[index].vx;
    node.vy = nextVelocity[index].vy;
    node.vz = nextVelocity[index].vz;
    node.x += node.vx * dt;
    node.y += node.vy * dt;
    node.z += node.vz * dt;
  });
}

function resolveNetImpact(
  nodes: NetNode[],
  ballPosition: number[],
  ballVelocity: number[],
  ballMassKg: number,
  ballRadiusM: number,
): number {
  const influenceRadius = Math.max(0.55, ballRadiusM * 5.5);
  let totalWeight = 0;
  const weighted: Array<{ node: NetNode; weight: number }> = [];
  for (const node of nodes) {
    if (node.anchored) continue;
    const distance = Math.hypot(node.x - ballPosition[0], node.y - ballPosition[1]);
    if (distance <= influenceRadius) {
      const weight = (1 - distance / influenceRadius) ** 2;
      weighted.push({ node, weight });
      totalWeight += weight;
    }
  }
  if (totalWeight <= 0) return 0;
  const incomingVz = Math.max(0, ballVelocity[2]);
  const impulseNs = incomingVz * ballMassKg * (1 - NET_RESTITUTION);
  for (const item of weighted) {
    const share = item.weight / totalWeight;
    item.node.vz += (impulseNs * share) / NET_MASS_KG;
    item.node.z += Math.min(0.24, ballRadiusM * 0.9) * share;
  }
  ballVelocity[2] = -incomingVz * NET_RESTITUTION;
  ballVelocity[0] *= 0.64;
  ballVelocity[1] *= 0.72;
  ballPosition[2] = NET_PLANE_Z_M - ballRadiusM * 0.8;
  return impulseNs;
}

function classifyGoalLineEntry(position: readonly number[], radiusM: number): PenaltyOutcome {
  const x = position[0];
  const y = position[1];
  const halfWidth = GOAL_WIDTH_M / 2;
  const postBand = Math.max(0.09, radiusM * 0.65);
  if (Math.abs(x) > halfWidth + radiusM) return 'wide';
  if (y - radiusM > GOAL_HEIGHT_M) return 'over';
  if (Math.abs(Math.abs(x) - halfWidth) <= postBand && y <= GOAL_HEIGHT_M + radiusM) return 'post';
  if (Math.abs(x) <= halfWidth && Math.abs(y - GOAL_HEIGHT_M) <= postBand) return 'crossbar';
  if (Math.abs(x) <= halfWidth && y >= radiusM * 0.35 && y <= GOAL_HEIGHT_M) return 'goal';
  return 'short';
}

function createBallFrame(t: number, position: number[], velocity: number[]): PenaltyBallFrame {
  return {
    t,
    position: asVec3(position),
    velocity: asVec3(velocity),
    speedMps: length(velocity),
  };
}

function snapshotNet(nodes: NetNode[]): NetNodeFrame[] {
  return nodes.map((node) => ({
    x: node.x,
    y: node.y,
    z: node.z,
    displacementM: node.z - node.restZ,
  }));
}

function measurePeakNetDepth(nodes: NetNode[]): number {
  return nodes.reduce((peak, node) => Math.max(peak, node.z - node.restZ), 0);
}

function radians(degrees: number): number {
  return degrees * Math.PI / 180;
}

function length(value: readonly number[]): number {
  return Math.hypot(value[0], value[1], value[2]);
}

function asVec3(value: readonly number[]): [number, number, number] {
  return [value[0], value[1], value[2]];
}
